import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getConsentApiBaseUrl } from '../../shared/utils/apiConfig';
import { trackAPIError } from '../../shared/utils/sentryTracking';

export interface BiometricConsentDefinition {
  definitionUuid: string;
  consentHandle: string;
  consentText: string;
  language: string;
}

export interface BiometricConsentResponse {
  data: BiometricConsentDefinition[];
}

export interface UseBiometricConsentReturn {
  isLoading: boolean;
  error: string | null;
  consentText: string | null;
  definitionUuid: string | null;
  fetchConsent: (sessionToken: string) => Promise<void>;
  clearError: () => void;
}

/**
 * Custom hook to fetch biometric consent text from the consent API
 *
 * @returns {UseBiometricConsentReturn} Hook state and methods
 */
export function useBiometricConsent(): UseBiometricConsentReturn {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentText, setConsentText] = useState<string | null>(null);
  const [definitionUuid, setDefinitionUuid] = useState<string | null>(null);

  const fetchConsent = useCallback(async (sessionToken: string) => {
    if (!sessionToken || sessionToken.trim() === '') {
      setError('Session token is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const language = i18n.language || 'en';
      const consentApiUrl = `${getConsentApiBaseUrl()}/v1/consents/definitions?consentHandle=biometric_consent&language=${language}`;

      const response = await fetch(consentApiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const apiMessage = errorBody?.errors?.[0]?.message || errorBody?.detail || null;
        const err = new Error(apiMessage ?? `Failed to fetch consent: ${response.status}`);
        (err as any).apiMessage = apiMessage;
        (err as any).httpStatus = response.status;
        throw err;
      }

      const data: BiometricConsentResponse = await response.json();

      if (!data.data || data.data.length === 0) {
        throw new Error('No consent definition found');
      }

      const consentDefinition = data.data[0];
      if (!consentDefinition) {
        throw new Error('No consent definition found');
      }

      setConsentText(consentDefinition.consentText);
      setDefinitionUuid(consentDefinition.definitionUuid);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      trackAPIError(err, 'GET /v1/consents/definitions');
      setError(errorMessage);
      setConsentText(null);
      setDefinitionUuid(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [i18n]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    consentText,
    definitionUuid,
    fetchConsent,
    clearError,
  };
}
